import { useState, useRef } from "react";
import { Mail, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Facebook } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { buildSocialUrl } from "@/lib/social-links";
import { trackFollowClick } from "@/lib/follow-prompt";


const schema = z.object({
  email: z.string().trim().email("Ugyldig email").max(255),
  consent: z.literal(true, { errorMap: () => ({ message: "Du skal acceptere vilkår" }) }),
});

export function NewsletterSignup({ compact = false }: { compact?: boolean }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [resentAt, setResentAt] = useState<number | null>(null);
  const lastSentRef = useRef<number>(0);

  const sendSubscribe = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const parsed = schema.safeParse({ email, consent });
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? "Tjek formularen");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("newsletter-subscribe", {
        body: { email: parsed.data.email },
      });
      if (error) throw error;
      setDone(true);
      lastSentRef.current = Date.now();
    } catch (err: any) {
      toast.error(err?.message || "Kunne ikke tilmelde – prøv igen");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    const since = Date.now() - lastSentRef.current;
    if (since < 60_000) {
      toast.error(`Vent ${Math.ceil((60_000 - since) / 1000)} sek før du sender igen.`);
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("newsletter-subscribe", { body: { email } });
      if (error) throw error;
      lastSentRef.current = Date.now();
      setResentAt(Date.now());
      toast.success("Bekræftelsesmail sendt igen");
    } catch (err: any) {
      toast.error(err?.message || "Kunne ikke gensende");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-lg bg-accent/15 border border-accent/40 p-4 space-y-3">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-primary-foreground">Næsten færdig!</p>
            <p className="text-primary-foreground/90">
              Vi har sendt en bekræftelses-email til <strong>{email}</strong>. Klik linket i mailen for at fuldføre tilmeldingen.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2 text-xs text-primary-foreground/80 bg-primary/40 rounded p-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-300" />
          <p>
            <strong>Tjek spamfilteret</strong> eller fanen "Reklamer", hvis du ikke ser mailen inden for et par minutter.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={resend}
          disabled={loading}
          className="bg-transparent text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/10"
        >
          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
          {resentAt ? "Sendt igen" : "Send bekræftelse igen"}
        </Button>
        <div className="pt-2 mt-2 border-t border-primary-foreground/10 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-primary-foreground/80">Følg os også — vi er kun lige startet:</p>
          <Button
            asChild
            size="sm"
            variant="accent"
            className="gap-2"
            onClick={() => trackFollowClick('facebook', 'newsletter-success')}
          >
            <a href={buildSocialUrl('facebook', 'newsletter-success')} target="_blank" rel="noopener noreferrer" aria-label="Følg Min Nye Elbil på Facebook">
              <Facebook className="w-3 h-3" /> Følg på Facebook
            </a>
          </Button>
        </div>

      </div>
    );
  }

  return (
    <form onSubmit={sendSubscribe} className="space-y-3">
      {!compact && (
        <p className="text-sm text-primary-foreground/70">
          Få nyt om elbiler, kampagner og guides direkte i indbakken.
        </p>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="email"
            placeholder="din@email.dk"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            className="pl-9 bg-background text-foreground"
            aria-label="Email-adresse"
          />
        </div>
        <Button type="submit" variant="accent" disabled={loading} className="whitespace-nowrap">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tilmeld"}
        </Button>
      </div>
      <label className="flex items-start gap-2 text-xs text-primary-foreground/70 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5"
          required
        />
        <span>
          Jeg accepterer at modtage nyhedsbrevet og kan afmelde når som helst. Du modtager en bekræftelsesmail — husk at tjekke spamfilteret.
        </span>
      </label>
    </form>
  );
}
